import React from 'react';
import {RawGenericDatePicker} from '../RawGenericDatePicker';
import {Icon} from '../../../../elements/Icon/Icon';
import {FormContainer} from '../../../form-containers/FormGroup';
import {FormErrorMessage} from '../../../form-labels/FormErrorMessage';
import {ErrorMessage} from 'formik';

/**
 * @param {object} param0
 * @param {boolean} [param0.raw]
 * If set to true, a non-Formik version of the form control will be injected instead.
 * FYI, PeriodDatePicker does not have a formik version! So make sure to always include the raw property.
 *
 * @param {React.ReactNode} [param0.label]
 * The text to be displayed as the label.
 *
 * @param {function} [param0.onChange]
 *  Set to a callback function with one argument, which stands for the date selected by the user. The function body should call the updater function returned by the useState hook to update the state.
 *
 * @param {Date | null | undefined} [param0.selected]
 * set to the selected date, stored in the state. It is similar to value prop on **<input>** elements.
 *
 * @param {boolean} [param0.showWeekNumbers=false]
 * Set to true to display week numbers alongside the calendar days in the timepicker. Week numbers indicate the week of the year each date falls into, providing additional context for users when selecting dates.
 *
 * @param {string} [param0.locale="en-US"]
 *  Sets the locale used by the timepicker for displaying dates and formatting. The value should be a string representing a valid locale identifier, such as "en-US" for English (United States). Providing the appropriate locale ensures that dates are displayed in the desired language and format for your users.
 *
 * @param {Date} [param0.minTime]
 * Sets the earliest selectable time in the timepicker.
 *
 * @param {string} [param0.validationError]
 * Error message to display when time picker validation fails
 *
 *  @param {Date} [param0.maxTime]
 * Sets the latest selectable time in the timepicker.
 *
 *  @param {string} [param0.timeCaption="Time"]
 * Sets the caption text for the time selector in the timepicker.
 *
 *  @param {number} [param0.tabIndex=0]
 * Sets the tab order for keyboard navigation within the timepicker component.
 */

export function TimePicker({
    disabled,
    id,
    label,
    locale,
    maxTime,
    minTime,
    name,
    onChange,
    selected,
    raw,
    showWeekNumbers,
    tabIndex,
    timeCaption,
    validationError,
}) {
    return (
        <FormContainer
            disabled={disabled}
            errorMessage={
                raw ? (
                    <>{validationError && <FormErrorMessage>{validationError}</FormErrorMessage>}</>
                ) : (
                    <ErrorMessage name={name} component={FormErrorMessage} />
                )
            }
            label={label}
            suffix={
                <>
                    <Icon name="schedule" />
                </>
            }>
            {raw ? (
                <RawGenericDatePicker
                    dateFormat={'HH:mm'}
                    disabled={disabled}
                    id={id}
                    locale={locale}
                    maxTime={maxTime}
                    minTime={minTime}
                    onChange={onChange}
                    selected={selected}
                    showTimeSelectOnly
                    showTimeSelect={true}
                    showWeekNumbers={showWeekNumbers}
                    tabIndex={tabIndex}
                    timeCaption={timeCaption}
                    timeIntervals={15}
                />
            ) : (
                <>Error</>
            )}
        </FormContainer>
    );
}
